// Strategy-owned classifier for why a month isn't paying. Centralizes the
// rule so all strategies emit the same taxonomy and the UI never has to
// re-derive it. Mirrors the day-classification order in `cmTwoStrategy`
// (and any future strategy that uses 2-ADL gating + a waiting period):
//   1. <2 ADL bucket consumes first
//   2. WP days are consumed next
//   3. Anything remaining is eligible
//
// Returns null when the month is paying (or could pay — the month's payout
// fields are the source of truth for that).

export interface DeriveIneligibilityReasonInput<TState = unknown> {
  facts: MonthlyCareFacts;
  openState: MonthlyState<TState>;
  closeState: MonthlyState<TState>;
  payout: PayoutSummary;
}

export function deriveIneligibilityReason<TState>(
  input: DeriveIneligibilityReasonInput<TState>,
): ProjectionIneligibilityReason | null {
  const { facts, openState, closeState, payout } = input;

  // A month that paid (regular indemnity or cost coverage) is not
  // ineligible. The EP lump month is a special case: it satisfies the WP
  // and pays retroactively, so we still classify it as `in-wp` because
  // the month itself didn't earn ongoing benefits — it reimbursed prior
  // ineligible months. Defer to the WP check below for that case.
  const paid = payout.indemnityPayout > 0 || payout.ltcCoveragePaid > 0;
  if (paid) return null;

  if (facts.non2AdlDays > 0) return 'lt-2-adl';

  const wpOpen =
    openState.waitingPeriod.homeDays > 0 || openState.waitingPeriod.facilityDays > 0;
  const wpClose =
    closeState.waitingPeriod.homeDays > 0 || closeState.waitingPeriod.facilityDays > 0;
  if (wpOpen || wpClose) return 'in-wp';

  return null;
}
